<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{ $lang('main.crud.import') }}</span>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <v-row>
          <v-col cols="12" md="2" xs="6">
            <v-date
              v-model="filters.from"
              :label="$lang('main.words.from')"
              scrollable
              clearable
            />
          </v-col>
          <v-col cols="12" md="2" xs="6">
            <v-date
              v-model="filters.to"
              :min="filters.from"
              :label="$lang('main.words.to')"
              scrollable
              clearable
            />
          </v-col>
          <v-col cols="12" xs="12" md="2">
            <c-reservation-sources
              v-model="filters.source_id"
              :label="$lang('reservations.filters.source')"
              :defaultSelection="true"
              :singleLine="true"
              :hideDetails="true"
              :clearable="true"
            />
          </v-col>
          <v-col cols="12" md="2" xs="12">
            <v-select
              v-model="filters.status"
              :items="$in('ConstReservationStatuses')"
              :label="$lang('reservations.filters.status')"
              clearable
            />
          </v-col>
          <v-col cols="12" xs="12" md="3">
            <v-text-field
              v-lazy-input:debounce="700"
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              :label="$lang('main.words.search')"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" xs="12" md="3">
            <v-select
              v-model="limit"
              :items="limits"
              :label="$lang('main.crud.limit')"
              hide-details
            />
          </v-col>
          <v-col cols="12" xs="12" md="3">
            <c-agencies-autocomplete
              v-model="filters.ref"
              :label="$lang('reservations.fields.referral')"
              clearable
            />
          </v-col>
        </v-row>

        <v-skeleton-loader
          v-show="!initialized"
          :tile="false"
          :boilerplate="false"
          type="table-row-divider@5"
          class="mx-auto"
        />

        <v-card v-show="initialized" flat>
          <v-card-text>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">
                      <v-checkbox v-model="all" dense hide-details class="mt-0"></v-checkbox>
                    </th>
                    <th class="text-left">
                      {{ $lang("reservations.fields.period") }}
                    </th>
                    <th class="text-left">
                      {{ $lang("reservations.fields.property") }}
                    </th>
                    <th class="text-left">
                      {{ $lang("reservations.fields.client") }}
                    </th>
                    <th class="text-center">
                      {{ $lang("reservations.fields.status") }}
                    </th>
                    <th class="text-left">
                      {{ $lang("reservations.fields.amount") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in pagination.items" :key="index">
                    <td class="text-center">
                      <v-checkbox
                        @change="select(item.id)"
                        :input-value="selected.indexOf(item.id) > -1"
                        dense hide-details
                        class="mt-0">
                      </v-checkbox>
                    </td>
                    <td class="text-left">
                      <v-chip color="secondary" small>
                        <v-avatar left>
                          <v-img :src="item.source.image_url"></v-img>
                        </v-avatar>
                        {{ $toDateFormat(item.start_date, 'DD/MM/YY') }}
                        <v-icon small color="primary">mdi-arrow-right</v-icon>
                        {{ $toDateFormat(item.end_date, 'DD/MM/YY') }}
                      </v-chip>
                    </td>
                    <td class="text-left">
                      <v-icon small class="mr-2">mdi-office-building</v-icon>
                      {{ item.property.name }} <br>
                      <v-icon small class="mr-2">mdi-bed</v-icon>{{ item.unit.name }}
                    </td>
                    <td class="text-left">
                      {{ item.client.name }}
                    </td>
                    <td class="text-center">
                      <v-icon
                        v-if="item.status === $const('ConstReservationStatuses', 'CONFIRMED')"
                        :title="$constText('ConstReservationStatuses', item.status)"
                        color="success">mdi-check-circle
                      </v-icon>
                      <v-icon
                        v-if="item.status === $const('ConstReservationStatuses', 'CANCELED')"
                        :title="$constText('ConstReservationStatuses', item.status)"
                        color="error">mdi-cancel
                      </v-icon>
                      <v-icon
                        v-if="item.status === $const('ConstReservationStatuses', 'PENDING')"
                        :title="$constText('ConstReservationStatuses', item.status)"
                        color="warning">mdi-timer-sand
                      </v-icon>
                    </td>
                    <td class="text-left">
                      {{ $money(item.amount, item.currency) }}
                    </td>
                  </tr>
                  <tr v-if="!collection.length">
                    <td colspan="5" class="text-center">{{ $lang("main.crud.no_records") }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-pagination
              v-if="pagination.total > limit"
              v-model="filters.page"
              :length="pagination.totalPages"
              :total-visible="7"
              circle
            />

          </v-card-text>
        </v-card>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="success" @click="submit()">{{ $lang("main.crud.import") }}</v-btn>
      <v-btn color="error" @click="cancel">{{ $lang("main.crud.cancel") }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { mapState } from "vuex";

  import {
    GET_RESERVATIONS_COLLECTION,
  } from "@/store/reservations/actions";

  import formable from "@/mixins/formable";

  export default {
    name: "ImportForm",
    mixins: [
      formable
    ],
    props: {
      companyId: {
        type: Number,
        required: true
      },
      searchFilter: {
        type: String,
        default: null
      }
    },
    data() {
      return {
        filters: {
          page: 1,
          to: null,
          from: this.$moment().format('YYYY-MM-01'),
          order: "desc",
          sortBy: 'created_at',
          search: this.searchFilter
        },
        limit: 5,
        limits: [
          { text: '5', value: 5},
          { text: '15', value: 15},
          { text: '50', value: 50},
          { text: 'All', value: -1},
        ],
        all: false,
        selected: [],
      }
    },
    beforeMount() {
      this.search();
    },
    methods: {
      search() {
        let params = {
          billable: true,
          ...this.filters
        };

        if (this.companyId) {
          params.company_id = this.companyId;
        }

        this.$store.dispatch(GET_RESERVATIONS_COLLECTION, params);
      },
      select(id) {
        const index = this.selected.indexOf(id);
        if (index > -1) {
          this.selected.splice(index, 1);
        } else {
          this.selected.push(id);
        }
      },
      submit() {
        let reservations = [];

        this.collection.map((item) => {
          if (this.selected.indexOf(item.id) > -1) {
            reservations.push(item);
          }
        });

        this.success(reservations);
      },
    },
    computed: {
      collection() {
        return this.request.collection;
      },
      pagination() {
        let items = this.collection;
        let totalPages = 0;

        if (this.limit !== -1) {
          totalPages = Math.ceil(this.collection.length / this.limit);
          const startIndex = this.limit * (this.filters.page - 1);
          items = items.slice(startIndex, startIndex + this.limit);
        }

        return {
          items: items,
          total: this.collection.length,
          totalPages: totalPages
        };
      },
      initialized() {
        return !this.request.loading;
      },
      ...mapState({
        request: state => state.reservations.getCollectionRequest,
      })
    },
    watch: {
      'all'(val) {
        if (!val) {
          this.selected = [];
        } else {
          this.selected = [];

          this.collection.map((item) => {
            this.selected.push(item.id);
          });
        }
      },
      filters: {
        handler() {
          this.search();
        },
        deep: true,
      },
    }
  }
</script>
